import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useOutletContext } from "react-router-dom";
import { portalApi, portalError } from "./portalApi";
import { Btn, Modal, Badge, Empty } from "../components/UI";
import { SignatureStamp } from "../components/SignaturePad";
import { SignaturePromptModal } from "../components/SignaturePromptModal";
import { FileSignature, ShieldCheck, PenLine } from "lucide-react";
import { toast } from "sonner";

export default function PortalConsents() {
  const { me } = useOutletContext();
  const [consents, setConsents] = useState([]);
  const [signing, setSigning] = useState(null); // { id, template_name, body } - consent to sign
  const [reviewOpen, setReviewOpen] = useState(false);
  const [sigOpen, setSigOpen] = useState(false);
  const [guardianOpen, setGuardianOpen] = useState(false);
  const [guardianSigOpen, setGuardianSigOpen] = useState(false);
  const [pendingClientSig, setPendingClientSig] = useState(null);
  const [guardianName, setGuardianName] = useState("");
  const [guardianRelationship, setGuardianRelationship] = useState("Parent");
  const [viewing, setViewing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => portalApi.get("/portal/consents").then((r) => setConsents(r.data));
  useEffect(() => { load(); }, []);

  // Compute client age from dob so the guardian step only appears for minors.
  const clientDob = me?.client?.dob || "";
  const isMinor = (() => {
    if (!clientDob) return false;
    try {
      const dob = dayjs(clientDob);
      return dayjs().diff(dob, "year") < 18;
    } catch { return false; }
  })();

  const openReview = (c) => { setSigning(c); setReviewOpen(true); };

  const proceedToSign = () => { setReviewOpen(false); setSigOpen(true); };

  const submitSignature = async ({ image, name }) => {
    if (!signing) return;
    // If the client is a minor, capture the guardian signature before submitting.
    if (isMinor) {
      setPendingClientSig({ image, name });
      setSigOpen(false);
      setGuardianName("");
      setGuardianRelationship("Parent");
      setGuardianOpen(true);
      return;
    }
    await postSignature({ image, name });
  };

  const postSignature = async ({ image, name, guardian }) => {
    setBusy(true);
    try {
      const payload = { name, image };
      if (guardian) {
        payload.guardian_signature = {
          name: guardian.name,
          image: guardian.image,
          relationship: guardian.relationship,
        };
      }
      const r = await portalApi.post(`/portal/consents/${signing.id}/sign`, payload);
      toast.success(`Signed. Verification ID ${r.data.signature?.signature_id}`);
      setSigOpen(false); setGuardianOpen(false); setSigning(null); setPendingClientSig(null);
      load();
    } catch (err) { toast.error(portalError(err)); }
    setBusy(false);
  };

  const submitGuardianSignature = async ({ image, name }) => {
    if (!pendingClientSig) return;
    if (!guardianName.trim() || guardianName.trim().length < 2) {
      toast.error("Please type the guardian's full legal name");
      return;
    }
    await postSignature({
      image: pendingClientSig.image,
      name: pendingClientSig.name,
      guardian: {
        name: guardianName.trim(),
        image,
        relationship: guardianRelationship,
      },
    });
  };

  const portalSignerName = `${me?.client?.first_name || ""} ${me?.client?.last_name || ""}`.trim();
  const pending = consents.filter((c) => c.status === "pending");
  const signed = consents.filter((c) => c.status === "signed");

  return (
    <div className="space-y-6" data-testid="portal-documents-page">
      <div>
        <h1 className="font-heading text-xl font-bold text-[#3E2723]">Documents and Consents</h1>
        <p className="text-sm text-[#7B6157]">Review and sign forms your clinic has sent you. Every signature is stamped with a verification ID, an SHA-256 document hash, and an ESIGN Act acknowledgement.</p>
      </div>

      <div>
        <h3 className="font-heading text-sm font-bold text-[#3E2723] mb-2">Awaiting Your Signature ({pending.length})</h3>
        {pending.length === 0 ? <p className="text-sm text-[#84675B]">Nothing to sign right now.</p> : (
          <div className="space-y-2">
            {pending.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center gap-3 rounded-xl bg-white border border-[#FFB300] px-4 py-3" data-testid={`portal-consent-${c.id}`}>
                <FileSignature size={18} className="text-[#8D5A00]" />
                <div>
                  <div className="font-bold text-[#3E2723] text-sm">{c.template_name}</div>
                  <div className="text-[11px] text-[#84675B]">Sent {dayjs(c.created_at).format("MMM D, YYYY")}</div>
                </div>
                <Btn data-testid={`portal-sign-btn-${c.id}`} className="ml-auto px-4 py-2 text-xs" onClick={() => openReview(c)}>Review and Sign</Btn>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="font-heading text-sm font-bold text-[#3E2723] mb-2">Signed Documents ({signed.length})</h3>
        {signed.length === 0 ? <Empty text="No signed documents yet" /> : (
          <div className="space-y-2">
            {signed.map((c) => (
              <button key={c.id} onClick={() => setViewing(c)} data-testid={`portal-signed-${c.id}`}
                className="flex w-full flex-wrap items-center gap-3 rounded-xl bg-white border border-[#EFEBE0] px-4 py-3 text-left hover:bg-[#FDFCF8]">
                <ShieldCheck size={17} className="text-[#2E7D32]" />
                <div>
                  <div className="font-bold text-[#3E2723] text-sm">{c.template_name}</div>
                  <div className="text-[11px] text-[#84675B]">Signed {(c.signature?.signed_at || "").slice(0, 10)} · ID {c.signature?.signature_id || "-"}</div>
                </div>
                <span className="ml-auto"><Badge value="signed" /></span>
              </button>
            ))}
          </div>
        )}
      </div>

      <Modal open={reviewOpen} onClose={() => setReviewOpen(false)} title={signing?.template_name || ""} wide>
        {signing && (
          <div className="space-y-4">
            <div className="max-h-72 overflow-y-auto rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] p-4 text-sm text-[#4E342E] whitespace-pre-wrap" data-testid="portal-consent-body">
              {signing.body}
            </div>
            <p className="text-[11px] text-[#84675B]">Read the document carefully. When you continue, you will be asked to draw, type, or upload your signature and confirm the ESIGN Act acknowledgement.</p>
            <div className="flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => setReviewOpen(false)}>Cancel</Btn>
              <Btn data-testid="portal-consent-continue" onClick={proceedToSign}>
                <PenLine size={13} /> Continue to Sign
              </Btn>
            </div>
          </div>
        )}
      </Modal>

      <SignaturePromptModal
        open={sigOpen}
        onClose={() => setSigOpen(false)}
        onConfirm={submitSignature}
        title={signing ? `Sign: ${signing.template_name}` : "Sign Document"}
        subtitle={isMinor
          ? "You are under 18. After you sign, a parent or legal guardian will be asked to co-sign."
          : "Your signature is stored with a verification ID, an SHA-256 hash, and a timestamp."}
        busy={busy}
        signerName={portalSignerName}
        allowSaveToProfile={false}
      />

      <Modal open={guardianOpen} onClose={() => { setGuardianOpen(false); setPendingClientSig(null); }}
        title="Parent or Guardian Co-Signature Required" wide>
        <div className="space-y-4" data-testid="portal-guardian-modal">
          <div className="rounded-xl border border-[#B26A00] bg-[#FFF8E1] p-4">
            <div className="text-xs font-bold uppercase tracking-wide text-[#8D5A00] mb-1">Two-signature consent</div>
            <p className="text-xs leading-relaxed text-[#4E342E]">
              Because {portalSignerName || "this client"} is under 18, a parent or legal guardian must co-sign this consent. Both signatures are stored and audit-logged with the same verification IDs and SHA-256 hashes.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs font-bold text-[#3E2723]" htmlFor="guardian-name">Guardian&apos;s full legal name</label>
              <input id="guardian-name" data-testid="portal-guardian-name" value={guardianName}
                onChange={(e) => setGuardianName(e.target.value)}
                className="mt-1 w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]"
                placeholder="For example, Jane A. Hassan" />
            </div>
            <div>
              <label className="text-xs font-bold text-[#3E2723]" htmlFor="guardian-relationship">Relationship to client</label>
              <select id="guardian-relationship" data-testid="portal-guardian-relationship" value={guardianRelationship}
                onChange={(e) => setGuardianRelationship(e.target.value)}
                className="mt-1 w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]">
                <option>Parent</option>
                <option>Legal Guardian</option>
                <option>Foster Parent</option>
                <option>Grandparent (guardian)</option>
                <option>Court-Appointed Advocate</option>
              </select>
            </div>
          </div>
          <div className="text-[11px] text-[#7B6157]">
            After you type the guardian&apos;s name and relationship, the guardian will draw or type their signature in the next step. Both signatures are recorded on the same consent.
          </div>
          <div className="flex justify-end">
            <Btn data-testid="portal-guardian-continue"
              onClick={() => {
                if (!guardianName.trim() || guardianName.trim().length < 2) {
                  toast.error("Please type the guardian's full legal name");
                  return;
                }
                // Reuse the SignaturePromptModal to capture the guardian's signature
                setGuardianOpen(false);
                setTimeout(() => setSigOpen(false), 0); // ensure closed
                setGuardianSigOpen(true);
              }}>
              Continue to Guardian Signature
            </Btn>
          </div>
        </div>
      </Modal>

      <SignaturePromptModal
        open={guardianSigOpen}
        onClose={() => setGuardianSigOpen(false)}
        onConfirm={submitGuardianSignature}
        title={`Guardian Signature: ${signing?.template_name || ""}`}
        subtitle="This signature is stored separately from the minor's signature and both are audit-logged."
        busy={busy}
        signerName={guardianName}
        allowSaveToProfile={false}
      />

      <Modal open={!!viewing} onClose={() => setViewing(null)} title={viewing?.template_name || ""} wide>
        {viewing && (
          <div className="space-y-4" data-testid="portal-signed-view">
            <div className="max-h-52 overflow-y-auto rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] p-4 text-sm text-[#4E342E] whitespace-pre-wrap">{viewing.body}</div>
            {viewing.hipaa_summary && (
              <div className="rounded-xl border border-[#C8E6C9] bg-[#F1F8E9] p-4" data-testid="portal-hipaa-summary">
                <div className="flex items-center gap-1.5 text-xs font-bold text-[#2E7D32] mb-1"><ShieldCheck size={13} /> HIPAA Disclosure Summary</div>
                <p className="text-xs leading-relaxed text-[#33691E]">{viewing.hipaa_summary}</p>
              </div>
            )}
            {viewing.signature?.image && <SignatureStamp sig={viewing.signature} label="Client Signature" />}
            {viewing.guardian_signature?.image && <SignatureStamp sig={viewing.guardian_signature} label="Guardian Signature" />}
          </div>
        )}
      </Modal>
    </div>
  );
}
